import { Info, Lock, MoreVertical, Pencil, Trash2 } from 'lucide-react'
import { getErrorMessage } from '@/api/errors'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useVariableGroups } from '@/hooks'
import { formatDateShort } from '@/lib/timeFormatters'
import type { VariableGroupSummary } from '@/types'
import { VariableGroupsEmptyState } from './VariableGroupsEmptyState'

interface VariableGroupsTabProps {
  projectId: string
  canManage: boolean
  onCreate: () => void
  onEdit: (group: VariableGroupSummary) => void
  onDelete: (group: VariableGroupSummary) => void
}

export function VariableGroupsTab({ projectId, canManage, onCreate, onEdit, onDelete }: VariableGroupsTabProps) {
  const { data: groups = [], isLoading, isError, error } = useVariableGroups(projectId)

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">Project Groups</h2>
        <p className="text-sm text-muted-foreground">
          Named sets of environment variables shared by the whole project. Each variable is either visible or secret.
        </p>
      </div>

      <div
        className="flex items-start gap-2 rounded border border-info-foreground bg-info px-3 py-2 text-sm text-info-foreground"
        data-testid="variable-groups-info"
      >
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Secret values are stored encrypted and are never shown again after saving. Any workflow run that uses a group can
          read its secrets at run time.
        </span>
      </div>

      {isLoading ? (
        <div className="space-y-3" role="status" aria-live="polite" aria-label="Loading environment variable groups">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : isError ? (
        <p role="alert" className="rounded border border-destructive px-3 py-2 text-sm text-destructive">
          {getErrorMessage(error) ?? 'Failed to load the environment variable groups.'}
        </p>
      ) : groups.length === 0 ? (
        <VariableGroupsEmptyState canManage={canManage} onCreate={onCreate} />
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <Table aria-label="Environment variable groups table">
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-[260px]">Group Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-[130px]">Variables</TableHead>
                <TableHead className="w-[180px]">Last Updated</TableHead>
                {canManage && <TableHead className="w-[90px]">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.map(group => (
                <TableRow key={group.id} data-testid={`variable-group-row-${group.name}`}>
                  <TableCell className="font-medium">
                    {canManage ? (
                      <button
                        type="button"
                        className="text-left text-foreground hover:underline"
                        onClick={() => onEdit(group)}
                      >
                        {group.name}
                      </button>
                    ) : (
                      group.name
                    )}
                  </TableCell>
                  <TableCell>{group.description}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span data-testid="variable-count">{group.variableCount}</span>
                      {group.secretVariableCount > 0 && (
                        <Badge
                          variant="outline"
                          className="gap-1"
                          aria-label={`${group.secretVariableCount} secret variables`}
                        >
                          <Lock className="h-3 w-3" aria-hidden="true" />
                          {group.secretVariableCount}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span>{formatDateShort(group.lastUpdatedAt)}</span>
                      {group.lastUpdatedBy && (
                        <span className="text-xs text-muted-foreground">{group.lastUpdatedBy}</span>
                      )}
                    </div>
                  </TableCell>
                  {canManage && (
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Actions for ${group.name}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => onEdit(group)}>
                            <Pencil className="h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onSelect={() => onDelete(group)}
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
